<ion-content>
  <ion-grid style="margin: 3px">
    <ion-row class="ion-justify-content-between ion-align-items-center" style="margin-bottom: 2rem;">
      <ion-col>
        <h1>Taxonomy Classification Dashboard</h1>
        <p style="margin: 0.25rem 0 0 0; font-size: 0.9rem;">Monitor the progress of automatic article categorization</p>
      </ion-col>
      <ion-col size="auto">
        <div style="display: flex; gap: 0.5rem;">
          <ion-button
            color="primary"
            (click)="startTaxonomyOnboarding()"
            [disabled]="isLoading">
            <ion-icon name="play-outline" slot="start"></ion-icon>
            Start Onboarding
          </ion-button>
          <ion-button
            color="danger"
            fill="outline"
            (click)="resetTaxonomy()"
            [disabled]="isLoading">
            <ion-icon name="trash-outline" slot="start"></ion-icon>
            Reset Taxonomy
          </ion-button>
          <ion-button
            color="secondary"
            fill="outline"
            routerLink="/admin/taxonomy-dashboard/system-confidence"
            [disabled]="isLoading">
            <ion-icon name="analytics-outline" slot="start"></ion-icon>
            System Analysis
          </ion-button>
          <ion-button
            fill="outline"
            (click)="onRefresh()"
            [disabled]="isLoading">
            <ion-icon name="refresh-outline" slot="start"></ion-icon>
            Refresh
          </ion-button>
        </div>
      </ion-col>
    </ion-row>

    <ion-row *ngIf="error">
      <ion-col>
        <ion-item color="danger">
          <ion-icon name="warning-outline" slot="start"></ion-icon>
          {{ error }}
        </ion-item>
      </ion-col>
    </ion-row>

    <ion-row *ngIf="isLoading" class="ion-justify-content-center" style="padding: 3rem;">
      <ion-col size="auto" class="ion-text-center">
        <ion-spinner color="primary"></ion-spinner>
        <p style="margin-top: 1rem;">Loading taxonomy statistics...</p>
      </ion-col>
    </ion-row>

    <ion-row *ngIf="!isLoading && !error && stats">
      <ion-col size="12" size-md="6">
        <!-- Overview Card -->
        <ion-card>
          <ion-card-header>
            <ion-card-title>Classification Overview</ion-card-title>
            <ion-card-subtitle>{{ stats.taxonomyStats.tenantId }}</ion-card-subtitle>
          </ion-card-header>
          <ion-card-content>
            <div style="margin-bottom: 2rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                <span style="font-weight: 500;">Classification Progress</span>
                <span style="font-weight: 600;">{{ stats.taxonomyStats.progressPercent }}%</span>
              </div>
              <ion-progress-bar
                [value]="stats.taxonomyStats.progressPercent / 100"
                [color]="getStatusColor(stats.taxonomyStats.progressPercent)">
              </ion-progress-bar>
              <div style="text-align: center; margin-top: 1rem;">
                <ion-chip [color]="getStatusColor(stats.taxonomyStats.progressPercent)">
                  {{ getStatusText(stats.taxonomyStats.progressPercent) }}
                </ion-chip>
              </div>
            </div>

            <ion-grid>
              <ion-row class="ion-text-center">
                <ion-col>
                  <div style="font-size: 2rem; font-weight: 700; line-height: 1.2;">{{ stats.taxonomyStats.totalArticles }}</div>
                  <div style="font-size: 0.875rem; margin-top: 0.25rem;">Total Articles</div>
                </ion-col>
                <ion-col>
                  <div style="font-size: 2rem; font-weight: 700; line-height: 1.2;">{{ stats.taxonomyStats.classifiedArticles }}</div>
                  <div style="font-size: 0.875rem; margin-top: 0.25rem;">Classified</div>
                </ion-col>
                <ion-col>
                  <div style="font-size: 2rem; font-weight: 700; line-height: 1.2;">{{ stats.taxonomyStats.totalArticles - stats.taxonomyStats.classifiedArticles }}</div>
                  <div style="font-size: 0.875rem; margin-top: 0.25rem;">Remaining</div>
                </ion-col>
              </ion-row>
            </ion-grid>
          </ion-card-content>
        </ion-card>
      </ion-col>

      <ion-col size="12" size-md="6">
        <!-- Strategy Settings Card -->
        <ion-card>
          <ion-card-header>
            <ion-card-title>Strategy Configuration</ion-card-title>
            <ion-card-subtitle>Current tenant classification settings</ion-card-subtitle>
          </ion-card-header>
          <ion-card-content>
            <div style="margin-bottom: 1.5rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                <span style="font-weight: 500;">Strategy</span>
                <ion-chip [color]="getStrategyColor(stats.strategySettings.strategy)">
                  {{ getStrategyLabel(stats.strategySettings.strategy) }}
                </ion-chip>
              </div>
              <div *ngIf="stats.strategySettings.strategyDescriptions" style="font-size: 0.8rem; color: var(--ion-color-medium); margin-bottom: 1rem;">
                {{ stats.strategySettings.strategyDescriptions[stats.strategySettings.strategy] }}
              </div>
            </div>

            <!-- Confidence Thresholds -->
            <div style="margin-bottom: 1rem;">
              <h4 style="margin: 0 0 0.5rem 0; font-size: 0.9rem; font-weight: 600;">Confidence Thresholds</h4>
              <div style="display: flex; justify-content: space-between; font-size: 0.8rem; margin-bottom: 0.25rem;">
                <span>LLM: {{ stats.strategySettings.confidenceThresholds.llm }}</span>
                <span>Centroid: {{ stats.strategySettings.confidenceThresholds.centroid }}</span>
                <span>Aggregation: {{ stats.strategySettings.confidenceThresholds.aggregation }}</span>
              </div>
            </div>

            <div style="font-size: 0.75rem; opacity: 0.7; text-align: center;">
              Strategy can be changed via database configuration
            </div>
          </ion-card-content>
        </ion-card>
      </ion-col>

      <ion-col size="12">
        <!-- Categories Card -->
        <ion-card>
          <ion-card-header>
            <ion-card-title>Categories</ion-card-title>
            <ion-card-subtitle>{{ stats.taxonomyStats.categories.length }} categories</ion-card-subtitle>
          </ion-card-header>
          <ion-card-content>
            <div *ngIf="stats.taxonomyStats.categories.length === 0" style="text-align: center; padding: 2rem;">
              <ion-icon name="folder-outline" style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem;"></ion-icon>
              <p style="margin: 0;">No categories found. Articles may not be classified yet.</p>
            </div>
            <div *ngIf="stats.taxonomyStats.categories.length > 0" style="max-height: 400px; overflow-y: auto;">
              <ion-item
                *ngFor="let category of stats.taxonomyStats.categories; trackBy: trackByCategory"
                lines="none"
                style="padding: 0.75rem 0; cursor: pointer;"
                (click)="viewCategoryAnalysis(category.category)">
                <ion-label>
                  <span style="font-weight: 500;">{{ category.category }}</span>
                </ion-label>
                <ion-chip slot="end" color="medium">
                  {{ category.articleCount }}
                </ion-chip>
                <ion-icon name="chevron-forward-outline" slot="end" style="margin-left: 0.5rem; opacity: 0.5;"></ion-icon>
              </ion-item>
            </div>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>

    <!-- Detailed Categories with Articles -->
    <ion-row *ngIf="!isLoadingCategories && !error && categories">
      <ion-col>
        <ion-card>
          <ion-card-header>
            <ion-card-title>Categories & Articles</ion-card-title>
            <ion-card-subtitle>{{ categories.categories.length }} categories with article details</ion-card-subtitle>
          </ion-card-header>
          <ion-card-content>
            <div *ngIf="categories.categories.length === 0" style="text-align: center; padding: 2rem;">
              <ion-icon name="document-outline" style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem;"></ion-icon>
              <p style="margin: 0;">No categories with articles found.</p>
            </div>
           
            <div *ngIf="categories.categories.length > 0">
              <div
                *ngFor="let category of categories.categories; trackBy: trackByCategoryDetailed"
                style="margin-bottom: 2rem; border: 1px solid var(--ion-color-light); border-radius: 8px; padding: 1rem;">
               
                <!-- Category Header -->
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                  <div style="display: flex; align-items: center; gap: 0.5rem;">
                    <h3 style="margin: 0; font-size: 1.1rem;">{{ category.category }}</h3>
                    <ion-chip [color]="getScopeColor(category)" size="small">
                      {{ getScopeLabel(category) }}
                    </ion-chip>
                  </div>
                  <ion-chip color="medium">
                    {{ category.articleCount }} articles
                  </ion-chip>
                </div>

                <!-- Articles List -->
                <div *ngIf="category.articles.length > 0" style="padding-left: 1rem;">
                  <div
                    *ngFor="let article of category.articles; trackBy: trackByArticle"
                    style="margin-bottom: 0.5rem;">
                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                      <button 
                        (click)="viewArticleAnalysis(article.id)"
                        style="background: none; border: none; color: var(--ion-color-primary); text-decoration: underline; cursor: pointer; font-weight: 500; padding: 0; text-align: left;">
                        {{ article.title }}
                      </button>
                      <a
                        [href]="getArticleUrl(article)"
                        target="_blank"
                        style="color: var(--ion-color-secondary); text-decoration: underline; font-size: 0.8rem;">
                        edit
                      </a>
                    </div>
                  </div>
                </div>

                <div *ngIf="category.articles.length === 0" style="padding-left: 1rem; color: var(--ion-color-medium);">
                  No articles found in this category
                </div>
              </div>
            </div>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>

    <ion-row *ngIf="isLoadingCategories" class="ion-justify-content-center" style="padding: 2rem;">
      <ion-col size="auto" class="ion-text-center">
        <ion-spinner color="primary"></ion-spinner>
        <p style="margin-top: 1rem;">Loading categories...</p>
      </ion-col>
    </ion-row>

    <!-- Metadata -->
    <ion-row *ngIf="!isLoading && !error && stats">
      <ion-col>
        <ion-card>
          <ion-card-content>
            <ion-grid>
              <ion-row>
                <ion-col size="12" size-md="4">
                  <div style="display: flex; flex-direction: column; gap: 0.25rem;">
                    <strong style="font-size: 0.875rem;">Description:</strong>
                    <span style="font-weight: 500;">{{ stats.meta.description }}</span>
                  </div>
                </ion-col>
                <ion-col size="12" size-md="4">
                  <div style="display: flex; flex-direction: column; gap: 0.25rem;">
                    <strong style="font-size: 0.875rem;">Last Updated:</strong>
                    <span style="font-weight: 500;">{{ stats.meta.lastUpdated | date:'medium' }}</span>
                  </div>
                </ion-col>
                <ion-col size="12" size-md="4">
                  <div style="display: flex; flex-direction: column; gap: 0.25rem;">
                    <strong style="font-size: 0.875rem;">Status:</strong>
                    <span style="font-weight: 500;">{{ stats.taxonomyStats.isComplete ? 'Complete' : 'In Progress' }}</span>
                  </div>
                </ion-col>
              </ion-row>
            </ion-grid>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>
  </ion-grid>
</ion-content>
